import React, { useMemo } from 'react';

import { Input, Form, InputNumber, Select, Checkbox, Tooltip } from 'antd';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import classnames from 'classnames';

import { QaSpecBoolean } from 'pages/qa-spec/components/shared/QaSpecBoolean';

import './BasicFormItem.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import {
  getFormattedFieldName,
  getFormattedPath,
} from 'pages/qa-spec/components/shared/QaSpecHelper';

const { TextArea } = Input;
const { Option } = Select;

const BasicFormItem = (props) => {
  const {
    name,
    label,
    rules = [],
    type,
    renderInput,
    selectProps = {
      options: [],
      inputProps: {},
    },
    inputProps = {},
    allowEdit = true,
    disabled = false,
    layout,
    showTooltip = false,
    fieldPath = '',
    ...restProps
  } = props;

  const isDisabled = disabled || !allowEdit;
  const inputPropsWithDisabled = {
    ...inputProps,
    disabled: isDisabled,
  };

  const newRuleItems = rules?.map((ruleItem = {}) => {
    let newRuleItem;

    const { type } = ruleItem;

    switch (type) {
      default:
        newRuleItem = ruleItem;
    }

    return newRuleItem;
  });

  const renderInputComponent = () => {
    if (renderInput) {
      return renderInput({ allowEdit, inputProps: inputPropsWithDisabled, name, disabled: isDisabled });
    }

    if (type === 'numeric') {
      return <InputNumber disabled={isDisabled} {...inputPropsWithDisabled} />;
    }

    if (type === 'textarea') {
      return <TextArea disabled={isDisabled} {...inputPropsWithDisabled} />;
    }

    if (type === 'checkbox') {
      return (
        <Checkbox
          className={classnames('basic-form-item__checkbox', {
            'basic-form-item__checkbox--no-edit': !allowEdit,
          })}
          disabled={isDisabled}
          {...inputPropsWithDisabled}
        />
      );
    }

    if (type === 'boolean-radio') {
      return (
        <QaSpecBoolean
          className={classnames('basic-form-item__checkbox', {
            'basic-form-item__checkbox--no-edit': !allowEdit,
          })}
          disabled={isDisabled}
          {...inputPropsWithDisabled}
        />
      );
    }

    if (type === 'select') {
      let customSelectProps = selectProps?.inputProps || {};
      let customOtptionsProps =
        selectProps?.optionProps ||
        function () {
          return {};
        };

      if (!allowEdit) {
        customSelectProps = {
          ...customSelectProps,
          open: false,
          showSearch: false,
          allowClear: false,
          disabled: isDisabled,
        };
      } else {
        customSelectProps = {
          ...customSelectProps,
          disabled: isDisabled,
        };
      }

      return (
        <Select {...customSelectProps}>
          {selectProps?.options?.map((optionItem, idx) => (
            <Option
              value={optionItem?.value}
              key={optionItem?.value}
              {...customOtptionsProps(optionItem)}
            >
              {optionItem?.displayName}
            </Option>
          ))}
        </Select>
      );
    }

    return (
      <InputWrapper showTooltip={showTooltip}>
        <Input disabled={isDisabled} readOnly={!allowEdit} {...inputPropsWithDisabled} />
      </InputWrapper>
    );
  };

  let defaultFormItemProps = useMemo(() => {
    let nextDefaultFormItemPropsProps = {};
    if (type === 'checkbox') {
      nextDefaultFormItemPropsProps = {
        ...nextDefaultFormItemPropsProps,
        valuePropName: 'checked',
      };

      if (layout === 'horizontal') {
        nextDefaultFormItemPropsProps = {
          ...nextDefaultFormItemPropsProps,
          htmlFor: name,
          label: (
            <span
              onClick={(e) => {
                name &&
                  document
                    .querySelector(`.basic-form-item__name-${name} input`)
                    ?.click();
              }}
            >
              {label && label}
            </span>
          ),
        };
      }
    }

    return nextDefaultFormItemPropsProps;
  }, [type, name, layout, label]);

  const { className, ...restDefaultFormItemProps } = defaultFormItemProps;

  return (
    <Form.Item
      shouldUpdate={true}
      className={classnames(
        'basic-form-item',
        `basic-form-item__name-${name}`,
        {
          'basic-form-item--no-edit': !allowEdit,
          'basic-form-item--horizontal-layout': layout === 'horizontal',
        }
      )}
      name={name}
      label={
        <TraceableCommentContainer
          qaSpecName={
            getFormattedFieldName(name) === 'Palm Oil' ||
            getFormattedFieldName(name) === 'Palm Oil Certified'
              ? 'Product Contains'
              : getFormattedPath(fieldPath)
          }
          qaSpecFieldName={getFormattedFieldName(name)}
        >
          <QaSpecHelpContentLabel fieldPath={fieldPath}>
            {label}
          </QaSpecHelpContentLabel>
        </TraceableCommentContainer>
      }
      rules={newRuleItems}
      {...restDefaultFormItemProps}
      {...restProps}
    >
      {renderInputComponent()}
    </Form.Item>
  );
};

export default BasicFormItem;

const InputWrapper = (props) => {
  const { children, showTooltip, ...restProps } = props;

  const { value, checked } = restProps || {};

  const inputValue = value || checked;

  const renderChildren = () => {
    return React.Children.map(props.children, (child) => {
      return React.cloneElement(child, restProps);
    });
  };

  if (!showTooltip) {
    return <>{renderChildren()}</>;
  }
  return <Tooltip title={inputValue}>{renderChildren()}</Tooltip>;
};